import { useTranslate } from "ra-core";
import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

interface ConversionRow {
  month: string;
  loans: number;
  converted: number;
  returned: number;
  lost: number;
  open: number;
  conversion_pct: number | null;
  conversion_all_pct: number | null;
}

const pct = (value: number | null) => (value == null ? "—" : `${value}%`);

// Conversion of the coach trials by month (loan_conversion view): on the
// trials with an outcome and on all of them, for the March 2027 decision
// (D-026: above 30% it stays, below 20% it closes)
export const LoanConversion = () => {
  const translate = useTranslate();
  const { data: rows = [] } = useQuery({
    queryKey: ["loan_conversion"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("loan_conversion")
        .select("*")
        .order("month", { ascending: false })
        .limit(24);
      if (error) throw error;
      return data as ConversionRow[];
    },
  });
  return (
    <section
      aria-label={translate("tenax.rackets.conversion.title")}
      className="flex flex-col gap-2"
    >
      <h2 className="text-lg font-semibold">
        {translate("tenax.rackets.conversion.title")}
      </h2>
      {rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                {(
                  [
                    "month",
                    "loans",
                    "converted",
                    "returned",
                    "lost",
                    "open",
                    "conversion_pct",
                    "conversion_all_pct",
                  ] as const
                ).map((key) => (
                  <th key={key} className="px-2 py-1 font-medium">
                    {translate(`tenax.rackets.conversion.${key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.month} className="border-t tabular-nums">
                  <td className="px-2 py-1">{row.month.slice(0, 7)}</td>
                  <td className="px-2 py-1">{row.loans}</td>
                  <td className="px-2 py-1">{row.converted}</td>
                  <td className="px-2 py-1">{row.returned}</td>
                  <td className="px-2 py-1">{row.lost}</td>
                  <td className="px-2 py-1">{row.open}</td>
                  <td className="px-2 py-1">{pct(row.conversion_pct)}</td>
                  <td className="px-2 py-1">{pct(row.conversion_all_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("tenax.rackets.conversion.empty")}
        </p>
      )}
    </section>
  );
};
